/**
 * Post-authentication redirect resolution (open-redirect safe).
 *
 * Priority: `?next=` → `?from=` (set by the axios 401 interceptor) → `location.state.from`
 * (set by route guards) → caller fallback. Only same-origin relative paths are accepted.
 */

export const NEXT_PARAM = 'next';
const FROM_PARAM = 'from';

// Redirecting back to an auth page after auth would loop.
const AUTH_PATHS = new Set(['/login', '/sign-up']);

interface LocationLike {
  search: string;
  state?: unknown;
}

interface StateLocation {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
}

/** Returns a normalised same-origin relative path, or null when `raw` is unsafe. */
export function toSafeRelativePath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  // Must be root-relative; reject protocol-relative (`//host`) and backslash tricks (`/\host`).
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;

  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    // Dot-segment normalisation can turn `/.//evil.com` into pathname `//evil.com`, which a
    // later navigation would treat as protocol-relative — re-check after normalising.
    if (url.pathname.startsWith('//') || url.pathname.includes('\\')) return null;
    if (AUTH_PATHS.has(url.pathname)) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

function pathFromState(state: unknown): string | null {
  const from = (state as { from?: StateLocation } | null | undefined)?.from;
  if (!from || typeof from.pathname !== 'string') return null;
  const search = typeof from.search === 'string' ? from.search : '';
  const hash = typeof from.hash === 'string' ? from.hash : '';
  return toSafeRelativePath(from.pathname + search + hash);
}

/** The explicitly requested post-auth destination, or null when none was requested. */
export function getRequestedRedirect(location: LocationLike): string | null {
  const params = new URLSearchParams(location.search);
  return (
    toSafeRelativePath(params.get(NEXT_PARAM))
    ?? toSafeRelativePath(params.get(FROM_PARAM))
    ?? pathFromState(location.state)
  );
}

export function getPostAuthRedirect(location: LocationLike, fallback = '/'): string {
  return getRequestedRedirect(location) ?? fallback;
}

/** Appends `?next=<path>` to an auth page link when a destination is known. */
export function withNextParam(authPath: string, next: string | null): string {
  if (!next) return authPath;
  return `${authPath}?${NEXT_PARAM}=${encodeURIComponent(next)}`;
}
